import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HashScroller } from "./lib/hash-scroller";
import { ScrollToTop } from "./lib/scroll-to-top";
import { Toaster } from "sonner";

import { AuthProvider } from "./lib/auth";
import { CartProvider } from "./lib/cart";
import { useCaptureAttribution } from "./lib/attribution";

import { Header } from "./components/layout/Header";
import { Footer } from "./components/layout/Footer";
import { CookieConsent } from "./components/consent/CookieConsent";
import { AnalyticsLoader } from "./components/consent/AnalyticsLoader";
import { FirstTimeVisitorGate } from "./components/consent/FirstTimeVisitorGate";
import { MiniCart } from "./components/cart/MiniCart";

import Home from "./pages/Home";
import ShopAll from "./pages/ShopAll";
import Categories from "./pages/Categories";
import ProductDetail from "./pages/ProductDetail";
import { Login, Register } from "./pages/AuthPages";
import AccountDashboard from "./pages/AccountDashboard";
import Cart from "./pages/Cart";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentPages";
import CheckoutShipping from "./pages/CheckoutShipping";
import Quality from "./pages/Quality";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import NotFound from "./pages/NotFound";
import ObsoleteGone from "./pages/ObsoleteGone";
import CoaLibrary from "./pages/CoaLibrary";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import ResearchEligibilityGate from "./pages/ResearchEligibilityGate";
import ResearchEligibilityDeclined from "./pages/ResearchEligibilityDeclined";
import {
  RegulatoryDisclaimer, ResearchUsePolicy, Terms, Privacy,
  ShippingPolicy, ReturnPolicy, WarrantyDisclaimer, AcceptableUse, Accessibility,
  CookiePolicy, StateRestrictions, RecallNotices, OrderTracking,
  Sitemap,
} from "./pages/LegalPages";
import { useVisitBeacon } from "./lib/useVisitBeacon";

// Admin screens ship in their own chunk so public pages load less JavaScript.
const adminPage = (name) => lazy(() => import("./pages/admin").then((m) => ({ default: m[name] })));
const AdminDashboard = adminPage("AdminDashboard");
const AdminBatches = adminPage("AdminBatches");
const AdminCOA = adminPage("AdminCOA");
const AdminRecalls = adminPage("AdminRecalls");
const AdminAuditLogs = adminPage("AdminAuditLogs");
const AdminSiteSettings = adminPage("AdminSiteSettings");
const AdminShippingSettings = adminPage("AdminShippingSettings");
const AdminWholesaleLeads = adminPage("AdminWholesaleLeads");
const AdminImageReview = adminPage("AdminImageReview");
const AdminOrders = adminPage("AdminOrders");
const AdminOrderDetail = adminPage("AdminOrderDetail");

function BeaconMount() { useVisitBeacon(); return null; }
function AttributionMount() { useCaptureAttribution(); return null; }
function LegacyCategoryRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/shop/category/${slug}`} replace />;
}

function App() {
  return (
    <div className="App min-h-screen bg-white text-brand-text">
      <BrowserRouter>
        <ScrollToTop />
        <HashScroller />
        <CartProvider>
          <AuthProvider>
            <BeaconMount />
            <AttributionMount />
            <AnalyticsLoader />
            <Header />
            <main>
              <Suspense fallback={<div className="min-h-[60vh]" data-testid="route-loading" />}>
              <Routes>
                <Route path="/" element={<FirstTimeVisitorGate><Home /></FirstTimeVisitorGate>} />
                <Route path="/shop" element={<FirstTimeVisitorGate><ShopAll /></FirstTimeVisitorGate>} />
                <Route path="/shop/category/:categorySlug" element={<FirstTimeVisitorGate><ShopAll /></FirstTimeVisitorGate>} />
                <Route path="/categories" element={<FirstTimeVisitorGate><Categories /></FirstTimeVisitorGate>} />
                <Route path="/category/:slug" element={<LegacyCategoryRedirect />} />
                <Route path="/product/:slug" element={<FirstTimeVisitorGate><ProductDetail /></FirstTimeVisitorGate>} />
                {/* Public COA library restored 2026-02. Every reviewed
                    batch is browsable at /coa-library with per-lot view +
                    download. Backend routes at /api/coa/{slug}/{lot}/... */}
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/coa-library" element={<CoaLibrary />} />
                <Route path="/coa-library/*" element={<CoaLibrary />} />
                <Route path="/coa/*" element={<CoaLibrary />} />
                {/* Reviews were never a supported storefront section. */}
                <Route path="/reviews" element={<ObsoleteGone kind="reviews" />} />
                <Route path="/reviews/*" element={<ObsoleteGone kind="reviews" />} />
                {/* Institutional / wholesale / apply-for-an-account content
                    retired 2026-08. Public visitors are redirected to /shop
                    (no application form is exposed). */}
                <Route path="/institutional-accounts"       element={<Navigate to="/shop" replace />} />
                <Route path="/institutional-accounts/*"     element={<Navigate to="/shop" replace />} />
                <Route path="/wholesale-accounts"           element={<Navigate to="/shop" replace />} />
                <Route path="/wholesale-accounts/*"         element={<Navigate to="/shop" replace />} />
                <Route path="/wholesale-estimator"          element={<Navigate to="/shop" replace />} />
                <Route path="/account-application"          element={<Navigate to="/shop" replace />} />
                <Route path="/account-application-received" element={<Navigate to="/shop" replace />} />
                <Route path="/quality" element={<Quality />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/account" element={<AccountDashboard />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout/shipping" element={<CheckoutShipping />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/products" element={<AdminDashboard />} />
                <Route path="/admin/batches" element={<AdminBatches />} />
                <Route path="/admin/coa" element={<AdminCOA />} />
                <Route path="/admin/recalls" element={<AdminRecalls />} />
                <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
                <Route path="/admin/site-settings" element={<AdminSiteSettings />} />
                <Route path="/admin/settings/shipping" element={<AdminShippingSettings />} />
                <Route path="/admin/wholesale-leads" element={<AdminWholesaleLeads />} />
                <Route path="/admin/image-review" element={<AdminImageReview />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/orders/:order_id" element={<AdminOrderDetail />} />
                <Route path="/regulatory-disclaimer" element={<RegulatoryDisclaimer />} />
                <Route path="/research-use-policy" element={<ResearchUsePolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/shipping-policy" element={<ShippingPolicy />} />
                <Route path="/return-policy" element={<ReturnPolicy />} />
                <Route path="/returns" element={<ReturnPolicy />} />
                <Route path="/warranty" element={<WarrantyDisclaimer />} />
                <Route path="/acceptable-use" element={<AcceptableUse />} />
                <Route path="/accessibility" element={<Accessibility />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/state-restrictions" element={<StateRestrictions />} />
                <Route path="/recall-notices" element={<RecallNotices />} />
                <Route path="/order-tracking" element={<OrderTracking />} />
                <Route path="/sitemap" element={<Sitemap />} />
                <Route path="/research-eligibility" element={<ResearchEligibilityGate />} />
                <Route path="/research-eligibility/declined" element={<ResearchEligibilityDeclined />} />
                <Route path="/checkout" element={<Navigate to="/checkout/shipping" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </main>
            <Footer />
            <CookieConsent />
            <Toaster theme="dark" position="bottom-right" richColors />
            <MiniCart />
          </AuthProvider>
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
